import React, { useCallback, useState } from 'react';
import { message } from 'antd';
import { useMutation } from '@apollo/client';

import { WorkingStyle } from 'components/HirerComponents/AnalyticsHub/TeamHiring/components/WinningWorkingStyles/types';
import { CandidatePublicProfile } from 'types';
import ProfilePic from 'components/CandidatePublicProfileComponents/ProfileCard/ProfilePic';
import Button from 'components/AntD/Button';
import { getVanityLinkDomain } from 'components/CandidateComponents/constants';
import { copyTextToClipboard } from 'components/SharedFunctions';
import { trackEvent } from 'analytics';
import {
  CANDIDATE_PUBLIC_PROFILE_SETTINGS_COPY_PUBLIC_URL,
  USER_PUBLIC_PROFILE_CLICK_REQUEST_REPORT,
  USER_PUBLIC_PROFILE_SUBMIT_REQUEST_REPORT,
} from 'enums/events';
import Link1 from 'Icons/Link1';
import MySearchlightStrengths from '../MySearchlightStrengths';
import WorkingStylesGrid from 'components/HirerComponents/AnalyticsHub/TeamHiring/components/WinningWorkingStyles/components/WorkingStylesGrid';
import CandidateSummaryCard from 'components/CandidatePublicProfileComponents/CandidateSummaryCard';
import SendSearchlightReportButton from '../SendSearchlightReportButton';
import FullReportModal from 'components/CandidatePublicProfileComponents/FullReportModal';
import { CREATE_CANDIDATE_SHARE_REQUEST } from 'api/public_profile_api';
import ReferenceSummaries from '../ReferenceSummaries';

interface PublicProfileProps {
  vanityLinkText: string;
  candidatePublicProfile: CandidatePublicProfile;
  isPublicView: boolean;
}

const PublicProfile = ({
  vanityLinkText,
  candidatePublicProfile,
  isPublicView,
}: PublicProfileProps) => {
  const [isFullReportModalOpen, setIsFullReportModalOpen] = useState(false);
  const [submitRequest] = useMutation(CREATE_CANDIDATE_SHARE_REQUEST);
  const requestReportCallback = useCallback(
    input => {
      try {
        submitRequest({
          variables: {
            input,
          },
        });
        trackEvent(USER_PUBLIC_PROFILE_SUBMIT_REQUEST_REPORT);
      } catch (e) {
        message.error('Something went wrong!');
      }
    },
    [submitRequest]
  );

  const {
    candidateDetails: { candidateFirstName, candidateLastName, candidatePhoto },
    referencerDetails,
    candidateStrengths = [],
    behaviorProfile,
  } = candidatePublicProfile;
  const possessiveNoun = isPublicView ? `${candidateFirstName}'s` : 'your';
  const noun = isPublicView ? candidateFirstName : 'you';
  const strengthsArray = candidateStrengths.map(
    ({ attribute: { attributeName, subtitle }, count }) => ({
      label: attributeName,
      subtitle,
      references: Array(count),
    })
  );

  const behaviorProfileToWorkingStyles: WorkingStyle[] = behaviorProfile.map(
    ({ firstAnswerOption, secondAnswerOption, firstAnswerOptionValue }) => ({
      traitName: firstAnswerOption.optionName,
      traitDescription: firstAnswerOption.subtitle,
      inverseTraitName: secondAnswerOption.optionName,
      inverseTraitDescription: secondAnswerOption.subtitle,
      topGroupScore: firstAnswerOptionValue,
    })
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-7 tw-flex-1">
      {/* Welcome Card */}
      <section className="tw-px-8 tw-py-5 md:tw-px-16 md:tw-py-10 tw-rounded tw-border tw-border-gray-300 tw-flex tw-flex-col tw-gap-2 tw-bg-white">
        <span className="tw-font-bold tw-text-lg">
          Welcome to {possessiveNoun} Searchlight profile!
        </span>
        <span className="tw-text-lg">
          This profile is a social resume of what it&apos;s like working with {noun}, according to
          the references {noun} collected.
        </span>
        <span className="tw-text-sm tw-text-gray-600">
          <a
            className="tw-text-gray-600 tw-underline"
            href="https://www.searchlight.ai"
            target="_blank"
            rel="noreferrer"
          >
            Searchlight
          </a>{' '}
          helps employers hire the right people with robust behavior and competency data. Read more
          about{' '}
          <a
            className="tw-text-gray-600 tw-underline"
            href="https://help.searchlight.ai/en/articles/6709358-how-to-interpret-searchlight-candidate-data"
            target="_blank"
            rel="noreferrer"
          >
            how hiring managers use our data
          </a>
          , or sign up for a{' '}
          <a
            className="tw-text-gray-600 tw-underline"
            href="https://app.searchlight.ai/sign-up"
            target="_blank"
            rel="noreferrer"
          >
            demo
          </a>
        </span>
      </section>
      <section className="tw-border-t-[20px] tw-border-gray-800 tw-px-16 tw-py-12 tw-rounded tw-bg-white tw-flex tw-flex-col tw-gap-11">
        <div className="tw-flex tw-flex-row tw-items-start tw-justify-between">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <ProfilePic
              candidateFirstName={candidateFirstName}
              candidatePhoto={candidatePhoto}
              canEdit={!isPublicView}
            />
            <h1 className="tw-text-[28px] tw-leading-7">
              {candidateFirstName} {candidateLastName}
            </h1>
          </div>
          <Button
            variant="contained"
            color="primary"
            className="tw-px-5 tw-py-2"
            onClick={() => {
              const copied = copyTextToClipboard(`${getVanityLinkDomain()}/${vanityLinkText}`);
              trackEvent(CANDIDATE_PUBLIC_PROFILE_SETTINGS_COPY_PUBLIC_URL);
              if (copied) {
                message.success('Copied link!');
              }
            }}
          >
            <Link1 color="white" width={20} height={20} /> Copy profile link
          </Button>
        </div>

        <ReferenceSummaries
          referencerDetails={referencerDetails}
          title={`Here's what ${possessiveNoun} past colleagues and managers have to say.`}
          isPublic={isPublicView}
        />
        <div className="tw-flex tw-flex-col tw-gap-4">
          <h5 className="tw-text-lg tw-font-semibold tw-capitalize">{possessiveNoun} Strengths</h5>
          <span>
            The thumbs up indicate the number of references who independently agree that {noun} has
            this trait.
          </span>
          <MySearchlightStrengths strengths={strengthsArray} />
        </div>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <h5 className="tw-text-lg tw-font-semibold tw-capitalize tw-flex tw-flex-col tw-gap-4">
            {possessiveNoun} Working Styles
          </h5>
          <span>
            Theses work styles describe an individual&apos;s behavioral inclinations at work. The
            dots represent the average answer provided by {possessiveNoun} references.
          </span>
          <div className="tw-max-2xl tw-m-auto">
            <WorkingStylesGrid data={behaviorProfileToWorkingStyles} />
          </div>
        </div>
      </section>
      {isPublicView ? (
        <section>
          <CandidateSummaryCard
            vanityLinkText={vanityLinkText}
            candidateFirstName={candidateFirstName}
            ctaCallback={() => {
              setIsFullReportModalOpen(true);
              trackEvent(USER_PUBLIC_PROFILE_CLICK_REQUEST_REPORT);
            }}
          />
        </section>
      ) : (
        <div className="tw-rounded-sm tw-bg-white tw-px-8 tw-py-5 tw-flex tw-flex-col tw-gap-4">
          <span className="tw-text-lg tw-font-semibold">
            Send your full Searchlight report to a recruiter or a hiring manager
          </span>
          <span className="tw-text-sm tw-text-gray-700">
            If you&apos;e actively interviewing, you can make a request to share your full reference
            report. We will automatically email the recipient to verify your request. You do not
            need to share your references with companies that have directly invited you to
            Searchlight.
          </span>
          <div>
            <SendSearchlightReportButton />
          </div>
        </div>
      )}
      <FullReportModal
        modalOpen={isFullReportModalOpen}
        requestReportCallback={requestReportCallback}
        onCancelBtnClick={() => setIsFullReportModalOpen(false)}
        candidateFirstName={candidateFirstName}
        vanityLinkText={vanityLinkText}
      />
    </div>
  );
};

export default PublicProfile;
